import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    AppBar,
    Box,
    CssBaseline,
    Drawer,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Toolbar,
    Typography,
    Button,
    Avatar,
    Menu,
    MenuItem,
    Divider,
} from '@mui/material';
import {
    Menu as MenuIcon,
    Dashboard as DashboardIcon,
    AccountTree as WorkflowIcon,
    PlayArrow as ExecutionsIcon,
    Person as PersonIcon,
    ExitToApp as LogoutIcon,
    Settings as SettingsIcon,
} from '@mui/icons-material';

const drawerWidth = 240;

interface LayoutProps {
    children: React.ReactNode;
    signOut?: () => void;
    user?: any;
}

export default function Layout({ children, signOut, user }: LayoutProps) {
    const [mobileOpen, setMobileOpen] = React.useState(false);
    const [userMenuAnchor, setUserMenuAnchor] = React.useState<null | HTMLElement>(null);
    const navigate = useNavigate();
    const location = useLocation();

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setUserMenuAnchor(event.currentTarget);
    };

    const handleUserMenuClose = () => {
        setUserMenuAnchor(null);
    };

    const handleSignOut = () => {
        handleUserMenuClose();
        if (signOut) {
            signOut();
        }
    };

    const menuItems = [
        { text: 'Dashboard', icon: <DashboardIcon />, path: '/' },
        { text: 'Workflows', icon: <WorkflowIcon />, path: '/workflows' },
        { text: 'Executions', icon: <ExecutionsIcon />, path: '/executions' },
    ];

    const drawer = (
        <div>
            <Toolbar sx={{
                display: 'flex',
                justifyContent: 'center',
                p: 1
            }}>
                <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
                    247ops.ai
                </Typography>
            </Toolbar>
            <Divider />
            <List>
                {menuItems.map((item) => (
                    <ListItem key={item.text} disablePadding>
                        <ListItemButton
                            onClick={() => navigate(item.path)}
                            selected={location.pathname === item.path ||
                                (item.path !== '/' && location.pathname.startsWith(item.path))}
                        >
                            <ListItemIcon>{item.icon}</ListItemIcon>
                            <ListItemText primary={item.text} />
                        </ListItemButton>
                    </ListItem>
                ))}
            </List>
        </div>
    );

    return (
        <Box sx={{ display: 'flex' }}>
            <CssBaseline />
            <AppBar
                position="fixed"
                sx={{
                    width: { sm: `calc(100% - ${drawerWidth}px)` },
                    ml: { sm: `${drawerWidth}px` },
                }}
            >
                <Toolbar>
                    <IconButton
                        color="inherit"
                        aria-label="open drawer"
                        edge="start"
                        onClick={handleDrawerToggle}
                        sx={{ mr: 2, display: { sm: 'none' } }}
                    >
                        <MenuIcon />
                    </IconButton>
                    <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
                        Workflow Automation
                    </Typography>
                    {user ? (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <IconButton
                                color="inherit"
                                onClick={handleUserMenuOpen}
                            >
                                <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.dark' }}>
                                    {user.username?.charAt(0).toUpperCase() || <PersonIcon />}
                                </Avatar>
                            </IconButton>
                            <Menu
                                anchorEl={userMenuAnchor}
                                open={Boolean(userMenuAnchor)}
                                onClose={handleUserMenuClose}
                                PaperProps={{
                                    elevation: 3,
                                    sx: { minWidth: 200 }
                                }}
                            >
                                <MenuItem disabled>
                                    <Typography variant="subtitle2">
                                        {user.username || 'User'}
                                    </Typography>
                                </MenuItem>
                                <Divider />
                                <MenuItem onClick={handleUserMenuClose}>
                                    <ListItemIcon>
                                        <SettingsIcon fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText primary="Settings" />
                                </MenuItem>
                                {signOut && (
                                    <MenuItem onClick={handleSignOut}>
                                        <ListItemIcon>
                                            <LogoutIcon fontSize="small" />
                                        </ListItemIcon>
                                        <ListItemText primary="Sign Out" />
                                    </MenuItem>
                                )}
                            </Menu>
                        </Box>
                    ) : (
                        <Button color="inherit" onClick={() => navigate('/')}>
                            Sign In
                        </Button>
                    )}
                </Toolbar>
            </AppBar>
            <Box
                component="nav"
                sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
            >
                <Drawer
                    variant="temporary"
                    open={mobileOpen}
                    onClose={handleDrawerToggle}
                    ModalProps={{
                        keepMounted: true, // Better open performance on mobile.
                    }}
                    sx={{
                        display: { xs: 'block', sm: 'none' },
                        '& .MuiDrawer-paper': {
                            boxSizing: 'border-box',
                            width: drawerWidth,
                        },
                    }}
                >
                    {drawer}
                </Drawer>
                <Drawer
                    variant="permanent"
                    sx={{
                        display: { xs: 'none', sm: 'block' },
                        '& .MuiDrawer-paper': {
                            boxSizing: 'border-box',
                            width: drawerWidth,
                        },
                    }}
                    open
                >
                    {drawer}
                </Drawer>
            </Box>
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    p: 3,
                    width: { sm: `calc(100% - ${drawerWidth}px)` },
                }}
            >
                <Toolbar />
                {children}
            </Box>
        </Box>
    );
}
